import React, { useState, useEffect } from 'react';
import { Card, DatePicker, Space, Typography, Spin, Alert, Row, Col, Statistic, Button, Skeleton, Select } from 'antd';
import { LineChartOutlined, CalendarOutlined, ArrowUpOutlined, ArrowDownOutlined, DollarOutlined, BankOutlined, StockOutlined } from '@ant-design/icons';
import ReactApexChart from 'react-apexcharts';
import moment from 'moment';
import dailyPriceService from '../services/dailyPriceService';

const { Text, Title } = Typography;
const { RangePicker } = DatePicker;

const formatKRW = (value) => {
  const number = Number(value);
  if (!Number.isFinite(number)) {
    return '';
  }
  return `₩${Math.round(number).toLocaleString()}`;
};

// 스켈레톤 차트 컴포넌트
const ChartSkeleton = () => (
  <div style={{ padding: '20px' }}>
    <Skeleton.Input style={{ width: '200px', height: '20px', marginBottom: '20px' }} active />
    <div style={{ height: '400px', backgroundColor: '#f5f5f5', borderRadius: '6px', position: 'relative' }}>
      <div style={{ 
        position: 'absolute', 
        top: '50%', 
        left: '50%', 
        transform: 'translate(-50%, -50%)',
        textAlign: 'center'
      }}>
        <Spin size="large" />
        <div style={{ marginTop: '16px', color: '#666' }}>
          차트 데이터를 불러오는 중...
        </div>
      </div>
    </div>
  </div>
);

// 통계 스켈레톤 컴포넌트
const StatsSkeleton = () => (
  <Row gutter={16} style={{ marginBottom: 24 }}>
    {[1, 2, 3, 4].map(i => (
      <Col xs={12} sm={6} key={i}>
        <Card>
          <Skeleton.Input style={{ width: '80px', height: '16px', marginBottom: '8px' }} active />
          <Skeleton.Input style={{ width: '120px', height: '24px' }} active />
        </Card>
      </Col>
    ))}
  </Row>
);

const PortfolioAnalysisChart = ({ 
  accounts = [], 
  accountsData = {}, 
  holdingsData = {}, 
  title = '포트폴리오 분석',
  hideAmounts = false
}) => {
  const [dateRange, setDateRange] = useState([
    moment().subtract(2, 'months'),
    moment()
  ]);
  const [portfolioData, setPortfolioData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [chartData, setChartData] = useState({ categories: [], series: [] });
  const [portfolioStats, setPortfolioStats] = useState(null);
  const [dataLoadingProgress, setDataLoadingProgress] = useState(0);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [chartMode, setChartMode] = useState('total');

  // 계좌 데이터가 있는지 확인
  const hasAccountData = accounts.length > 0 && Object.keys(accountsData).length > 0;
  const hasHoldingsData = Object.keys(holdingsData).length > 0;

  // 날짜 범위 변경 시 데이터 로드
  useEffect(() => {
    if (hasAccountData && hasHoldingsData && dateRange && dateRange.length === 2) {
      loadPortfolioData();
    }
  }, [accounts, accountsData, holdingsData, dateRange, chartMode]);

  // 초기 로드 상태 관리
  useEffect(() => {
    if (hasAccountData && hasHoldingsData) {
      setIsInitialLoad(false);
    }
  }, [hasAccountData, hasHoldingsData]);

  // 보유 종목 심볼 추출
  const getAllHoldingSymbols = () => {
    const allSymbols = new Set();
    accounts.forEach(account => {
      const holdingsResponse = holdingsData[account.accountId];
      if (holdingsResponse && holdingsResponse.holdings && Array.isArray(holdingsResponse.holdings)) {
        holdingsResponse.holdings.forEach(holding => {
          if (holding.symbol) {
            allSymbols.add(holding.symbol);
          }
        });
      }
    });
    return Array.from(allSymbols);
  };

  // 특정 날짜 또는 그 이전의 가장 최근 가격 찾기 (forward fill)
  const findLatestPrice = (priceData, targetDate) => {
    if (!priceData) return null;
    
    // 해당 날짜에 데이터가 있으면 바로 반환
    if (priceData[targetDate]) {
      return parseFloat(priceData[targetDate].closing_price);
    }
    
    // 해당 날짜에 데이터가 없으면 이전 날짜들을 역순으로 검색
    const targetMoment = moment(targetDate);
    const sortedDates = Object.keys(priceData).sort().reverse();
    
    for (const date of sortedDates) {
      if (moment(date).isSameOrBefore(targetMoment)) {
        return parseFloat(priceData[date].closing_price);
      }
    }
    
    return null;
  };

  // 특정 날짜의 보유 종목 가치 계산 (기존 방식)
  const calculatePortfolioValue = (pricesByDate, targetDate) => {
    let totalValue = 0;
    let totalCash = 0;
    let totalPnL = 0;
    let totalStocks = 0;
    let hasValidPriceData = false;

    accounts.forEach(account => {
      const accountData = accountsData[account.accountId] || {};
      const holdingsResponse = holdingsData[account.accountId];
      
      // 현금 자산 (가용 자금) - 항상 현재 값 사용
      totalCash += accountData.available_balance || 0;
      
      if (holdingsResponse && holdingsResponse.holdings && Array.isArray(holdingsResponse.holdings)) {
        holdingsResponse.holdings.forEach((holding, index) => {
          if (holding.symbol && holding.quantity) {
            totalStocks += 1;
            
            // 해당 날짜 또는 그 이전의 가장 최근 가격 찾기
            const priceData = pricesByDate[holding.symbol];
            const closingPrice = findLatestPrice(priceData, targetDate);
            
            if (closingPrice !== null) {
              const holdingValue = closingPrice * holding.quantity;
              totalValue += holdingValue;
              hasValidPriceData = true;
              
              // 다양한 매수가 필드명 시도
              const purchasePrice = holding.purchase_price || 
                                   holding.avg_price || 
                                   holding.average_price || 
                                   holding.buy_price ||
                                   holding.cost_price ||
                                   holding.pchs_avg_pric || // KIS API 필드
                                   holding.evaluation_amount / holding.quantity - (holding.profit_loss || 0) / holding.quantity || // 역산
                                   0;
              
              if (purchasePrice > 0) {
                const dailyPnL = (closingPrice - purchasePrice) * holding.quantity;
                totalPnL += dailyPnL;
              } else {
                // 매수가를 찾을 수 없으면 현재 손익을 모든 날짜에 적용 (차트 표시를 위해)
                if (holding.profit_loss) {
                  totalPnL += holding.profit_loss;
                }
              }
            } else {
              // 과거 데이터가 전혀 없는 경우에만 현재 평가금액 사용
              totalValue += holding.evaluation_amount || 0;
              // 현재 날짜에만 현재 손익 적용
              const isCurrentDate = targetDate === moment().format('YYYY-MM-DD');
              if (isCurrentDate && holding.profit_loss) {
                totalPnL += holding.profit_loss;
              }
            }
          }
        });
      }
    });

    return {
      totalAssets: totalValue + totalCash,
      availableCash: totalCash,
      stockValue: totalValue,
      totalPnL: totalPnL,
      totalStocks: totalStocks,
      hasValidPriceData: hasValidPriceData
    };
  };

  // 새로운 API를 사용한 포트폴리오 데이터 로드 (하이브리드 방식)
  const loadPortfolioData = async () => {
    if (accounts.length === 0) {
      return;
    }

    setLoading(true);
    setError(null);
    setDataLoadingProgress(0);

    try {
      const dateFrom = dateRange[0].format('YYYY-MM-DD');
      const dateTo = dateRange[1].format('YYYY-MM-DD');

      // console.log(`📊 포트폴리오 데이터 로드 (하이브리드):`, { dateFrom, dateTo });

      // 1단계: 새로운 API로 실제 보유량 기반 데이터 조회 시도
      setDataLoadingProgress(20);
      
      let portfolioTrendData = null;
      let useNewAPI = false;
      let trendResponse = null;
      
      try {
        const accountIds = accounts.map(account => account.accountId);
        trendResponse = await dailyPriceService.getPortfolioTrend({
          date_from: dateFrom,
          date_to: dateTo,
          account_ids: accountIds,
          ...(chartMode === 'account' ? { group_by: 'account' } : {})
        });
        
        if (trendResponse.success && trendResponse.data && trendResponse.data.length > 0) {
          portfolioTrendData = trendResponse.data;
          useNewAPI = true;
          // console.log('✅ 새로운 API 데이터 사용:', trendResponse.stats);
        }
      } catch (apiError) {
        console.warn('⚠️ 새로운 API 호출 실패, 기존 방식으로 폴백:', apiError.message);
      }

      setDataLoadingProgress(40);

      let portfolioHistory = [];

      if (useNewAPI && portfolioTrendData) {
        // 2-A: 새로운 API 데이터 사용
        // console.log('📈 실제 보유량 기반 데이터 사용');
        
        if (trendResponse?.mode === 'account') {
          const accountSeries = portfolioTrendData.map(accountTrend => ({
            name: accountTrend.account_name || `계좌 ${accountTrend.account_id}`,
            type: 'line',
            data: (accountTrend.trend || []).map(item => Number(item.total_assets) || 0)
          }));
          const firstAccountTrend = portfolioTrendData[0]?.trend || [];
          const categories = firstAccountTrend.map(item => item.date);
          const totalsByDate = {};

          portfolioTrendData.forEach(accountTrend => {
            (accountTrend.trend || []).forEach(item => {
              if (!totalsByDate[item.date]) {
                totalsByDate[item.date] = {
                  date: item.date,
                  totalAssets: 0,
                  availableCash: 0,
                  stockValue: 0,
                  totalPnL: 0,
                  totalStocks: 0,
                  hasValidPriceData: false,
                  dataSource: 'account_api'
                };
              }
              totalsByDate[item.date].totalAssets += Number(item.total_assets) || 0;
              totalsByDate[item.date].availableCash += Number(item.cash_balance || item.total_cash_balance) || 0;
              totalsByDate[item.date].stockValue += Number(item.total_market_value) || 0;
              totalsByDate[item.date].totalPnL += Number(item.total_unrealized_pnl) || 0;
              totalsByDate[item.date].totalStocks += Number(item.total_stocks) || 0;
              totalsByDate[item.date].hasValidPriceData = totalsByDate[item.date].hasValidPriceData || item.hasValidData !== false;
            });
          });

          portfolioHistory = Object.values(totalsByDate).sort((a, b) => a.date.localeCompare(b.date));
          setChartData({ categories, series: accountSeries });
          setPortfolioData(portfolioHistory);
          calculatePortfolioStats(portfolioHistory);
          setDataLoadingProgress(100);
          setLoading(false);
          return;
        }

        portfolioHistory = portfolioTrendData.map(item => ({
          date: item.date,
          // 새 API 필드명 매핑
          totalAssets: item.total_assets || item.totalAssets || 0,
          availableCash: item.total_cash_balance || item.availableCash || 0,
          stockValue: item.total_market_value || item.stockValue || 0,
          totalPnL: item.total_unrealized_pnl || item.totalPnL || 0,
          totalStocks: item.total_stocks || item.holdingsCount || 0,
          hasValidPriceData: item.hasValidData !== false,
          dataSource: item.dataSource || 'api'
        }));

        setDataLoadingProgress(90);
        
      } else {
        // 2-B: 기존 방식으로 폴백
        // console.log('📊 기존 종가 기반 계산 방식 사용');
        
        // Forward fill을 위해 시작 날짜보다 5일 더 이전부터 데이터 조회
        // .clone()으로 복사 후 subtract → 원본 moment 객체 뮤테이션 방지
        const extendedDateFrom = dateRange[0].clone().subtract(5, 'days').format('YYYY-MM-DD');
        
        // 모든 보유 종목의 종가 데이터 조회
        const holdingSymbols = getAllHoldingSymbols();
        
        if (holdingSymbols.length === 0) {
          setPortfolioData([]);
          setChartData({ categories: [], series: [] });
          setPortfolioStats(null);
          setDataLoadingProgress(100);
          return;
        }

        // 종목별로 순차적으로 데이터 로드 (진행률 표시)
        const pricesByDate = {};
        let completedCount = 0;

        for (const symbol of holdingSymbols) {
          try {
            const response = await dailyPriceService.getStockPriceHistory(
              symbol,
              extendedDateFrom,
              dateTo,
              'kis'
            );
            
            if (response.data.results && response.data.results.length > 0) {
              pricesByDate[symbol] = {};
              response.data.results.forEach(priceItem => {
                pricesByDate[symbol][priceItem.date] = priceItem;
              });
            }
            
            completedCount++;
            setDataLoadingProgress(Math.round((completedCount / holdingSymbols.length) * 80)); // 80%까지는 데이터 로드
            
          } catch (error) {
            console.error(`${symbol} 데이터 로드 실패:`, error);
            completedCount++;
            setDataLoadingProgress(Math.round((completedCount / holdingSymbols.length) * 80));
          }
        }

        // 날짜별 포트폴리오 가치 계산
        const dateList = [];
        const currentDate = moment(dateFrom);
        const endDate = moment(dateTo);

        while (currentDate.isSameOrBefore(endDate)) {
          dateList.push(currentDate.format('YYYY-MM-DD'));
          currentDate.add(1, 'day');
        }

        setDataLoadingProgress(85); // 계산 시작

        portfolioHistory = [];
        dateList.forEach(date => {
          const portfolioValue = calculatePortfolioValue(pricesByDate, date);
          portfolioHistory.push({
            date,
            ...portfolioValue,
            dataSource: 'legacy_calculation'
          });
        });

        setDataLoadingProgress(90);
      }

      // 공통 처리: 차트 데이터 포맷팅
      setPortfolioData(portfolioHistory);

      if (portfolioHistory.length > 0) {
        // 시작 부분의 0값들을 필터링 (주말/휴일로 인한 의미 없는 데이터 제거)
        // 첫 번째로 유효한 데이터가 나올 때까지 건너뛰기
        let firstValidIndex = 0;
        for (let i = 0; i < portfolioHistory.length; i++) {
          const item = portfolioHistory[i];
          // totalAssets가 0이 아니거나 stockValue가 0이 아니면 유효한 데이터로 간주
          if (item.totalAssets > 0 || item.stockValue > 0) {
            firstValidIndex = i;
            break;
          }
        }

        // 유효한 데이터만 필터링
        const filteredHistory = portfolioHistory.slice(firstValidIndex);

        // console.log('📊 차트 데이터 생성 중:', {
        //   originalLength: portfolioHistory.length,
        //   filteredLength: filteredHistory.length,
        //   firstValidDate: filteredHistory[0]?.date,
        //   firstItem: filteredHistory[0],
        //   lastItem: filteredHistory[filteredHistory.length - 1]
        // });

        // ApexCharts용 데이터 포맷 - categories + 일반 배열 방식 사용
        // {x, y} + type:'datetime' 조합은 날짜 파싱 불일치로 잘못된 SVG path 생성 → 분리
        const categories = filteredHistory.map(item => item.date);
        const totalAssetsData = filteredHistory.map(item => Number(item.totalAssets) || 0);
        const stockValueData  = filteredHistory.map(item => Number(item.stockValue)  || 0);
        const availableCashData = filteredHistory.map(item => Number(item.availableCash) || 0);
        const totalPnLData    = filteredHistory.map(item => Number(item.totalPnL)    || 0);

        // console.log('📊 변환된 차트 데이터:', {
        //   totalAssetsData: totalAssetsData.slice(0, 3),
        //   stockValueData: stockValueData.slice(0, 3),
        //   availableCashData: availableCashData.slice(0, 3),
        //   totalPnLData: totalPnLData.slice(0, 3)
        // });

        setChartData({
          categories,
          series: [
            { name: '총 자산',  type: 'line', data: totalAssetsData },
            { name: '주식 가치', type: 'line', data: stockValueData },
            { name: '가용 자금', type: 'line', data: availableCashData },
            { name: '총 손익',  type: 'line', data: totalPnLData }
          ]
        });
      } else {
        // console.log('📊 데이터가 없어서 빈 차트 설정');
        setChartData({ series: [] });
      }

      setDataLoadingProgress(95); // 통계 계산

      // 통계 계산
      calculatePortfolioStats(portfolioHistory);

      setDataLoadingProgress(100); // 완료
      // console.log('✅ 포트폴리오 데이터 로드 완료:', portfolioHistory.length, '일');
      
    } catch (error) {
      console.error('❌ 포트폴리오 데이터 로드 실패:', error);
      setError(error.message || '포트폴리오 데이터를 불러올 수 없습니다.');
      setPortfolioData([]);
      setChartData({ categories: [], series: [] });
      setPortfolioStats(null);
      setDataLoadingProgress(0);
    } finally {
      setLoading(false);
    }
  };

  const calculatePortfolioStats = (data) => {
    if (!data || data.length === 0) {
      setPortfolioStats(null);
      return;
    }

    // 첫째 날과 마지막 날 선택 (forward fill로 인해 모든 날짜에 데이터가 있음)
    const firstDay = data[0];
    const lastDay = data[data.length - 1];
    
    // console.log('📊 포트폴리오 통계 계산:', {
    //   firstDay: { 
    //     date: firstDay.date, 
    //     totalAssets: firstDay.totalAssets,
    //     stockValue: firstDay.stockValue,
    //     availableCash: firstDay.availableCash
    //   },
    //   lastDay: { 
    //     date: lastDay.date, 
    //     totalAssets: lastDay.totalAssets,
    //     stockValue: lastDay.stockValue,
    //     availableCash: lastDay.availableCash
    //   },
    //   totalDataCount: data.length
    // });
    
    const totalAssetsChange = lastDay.totalAssets - firstDay.totalAssets;
    const totalAssetsChangePercent = firstDay.totalAssets !== 0 ? (totalAssetsChange / firstDay.totalAssets) * 100 : 0;
    
    const stockValueChange = lastDay.stockValue - firstDay.stockValue;
    const stockValueChangePercent = firstDay.stockValue !== 0 ? (stockValueChange / firstDay.stockValue) * 100 : 0;

    const maxTotalAssets = Math.max(...data.map(item => item.totalAssets));
    const minTotalAssets = Math.min(...data.map(item => item.totalAssets));

    setPortfolioStats({
      firstDayAssets: firstDay.totalAssets,
      lastDayAssets: lastDay.totalAssets,
      totalAssetsChange,
      totalAssetsChangePercent,
      stockValueChange,
      stockValueChangePercent,
      maxTotalAssets,
      minTotalAssets,
      currentPnL: lastDay.totalPnL,
      dataCount: data.length,
      firstValidDate: firstDay.date,
      lastValidDate: lastDay.date
    });
  };

  // 차트 옵션을 동적으로 생성 (데이터가 있을 때만)
  const getChartOptions = () => {
    if (!chartData.series || chartData.series.length === 0) {
      return {};
    }

    const yaxis = chartMode === 'account'
      ? [{
          title: {
            text: '자산 (원)'
          },
          labels: {
            formatter: formatKRW
          }
        }]
      : [
          {
            title: {
              text: '자산 (원)'
            },
            labels: {
              formatter: formatKRW
            }
          },
          {
            show: false,
            labels: {
              formatter: formatKRW
            }
          },
          {
            opposite: true,
            title: {
              text: '가용자금 / 손익 (원)'
            },
            labels: {
              formatter: formatKRW
            }
          },
          {
            opposite: true,
            show: false,
            labels: {
              formatter: formatKRW
            }
          }
        ];

    return {
      chart: {
        type: 'line',
        height: 400,
        // 애니메이션 비활성화 - 업데이트 시 SVG path 좌표 오류 방지
        animations: { enabled: false },
        toolbar: {
          show: true,
          tools: {
            download: true,
            selection: true,
            zoom: true,
            zoomin: true,
            zoomout: true,
            pan: true,
            reset: true
          }
        },
        zoom: {
          enabled: true,
          type: 'x',
          autoScaleYaxis: true
        }
      },
      title: {
        text: chartMode === 'account' ? '계좌별 자산 변화' : '포트폴리오 자산 변화',
        align: 'left',
        style: {
          fontSize: '16px',
          fontWeight: 'bold'
        }
      },
      // type:'datetime' + categories 조합이 SVG path 좌표 오류를 일으킴
      // → categories 방식으로 변환, datetime 제거
      xaxis: {
        categories: chartData.categories || [],
        type: 'category',
        labels: {
          rotate: -45,
          formatter: function (value) {
            // "YYYY-MM-DD" → "MM/DD" 형식으로 표시
            if (!value) return '';
            const parts = String(value).split('-');
            if (parts.length === 3) return `${parts[1]}/${parts[2]}`;
            return value;
          },
          style: { fontSize: '11px' }
        },
        tickAmount: 8
      },
      yaxis,
      stroke: {
        width: [3, 2, 2, 2],
        // smooth 베지어 곡선이 큰 수치(억 원 단위)에서 비정상 SVG 좌표 생성 → straight로 변경
        curve: 'straight'
      },
      colors: ['#1890ff', '#52c41a', '#faad14', '#f5222d'],
      legend: {
        show: true,
        position: 'top',
        horizontalAlign: 'left'
      },
      tooltip: {
        shared: true,
        intersect: false,
        y: {
          formatter: formatKRW
        }
      },
      grid: {
        borderColor: '#f1f1f1'
      },
      noData: {
        text: '데이터가 없습니다',
        align: 'center',
        verticalAlign: 'middle',
        style: {
          color: '#666',
          fontSize: '14px'
        }
      }
    };
  };

  const handleDateRangeChange = (dates) => {
    setDateRange(dates);
  };

  // 프리셋 날짜 범위 설정 함수들
  const setThisMonth = () => {
    const startOfMonth = moment().startOf('month');
    const today = moment();
    setDateRange([startOfMonth, today]);
  };

  const setLastMonth = () => {
    const oneMonthAgo = moment().subtract(1, 'month');
    const today = moment();
    setDateRange([oneMonthAgo, today]);
  };

  const setLastThreeMonths = () => {
    const threeMonthsAgo = moment().subtract(3, 'months');
    const today = moment();
    setDateRange([threeMonthsAgo, today]);
  };

  const setLastSixMonths = () => {
    const sixMonthsAgo = moment().subtract(6, 'months');
    const today = moment();
    setDateRange([sixMonthsAgo, today]);
  };

  const setLastYear = () => {
    const oneYearAgo = moment().subtract(1, 'year');
    const today = moment();
    setDateRange([oneYearAgo, today]);
  };

  // 년도별 선택 함수들
  const setYearRange = (year) => {
    const startOfYear = moment().year(year).startOf('year');
    const endOfYear = moment().year(year).endOf('year');
    const today = moment();
    
    // 현재 년도인 경우 오늘까지만, 과거 년도인 경우 년말까지
    const endDate = year === moment().year() ? today : endOfYear;
    
    setDateRange([startOfYear, endDate]);
  };

  // 사용 가능한 년도 목록 생성 (현재 년도부터 5년 전까지)
  const getAvailableYears = () => {
    const currentYear = moment().year();
    const years = [];
    for (let i = 0; i <= 5; i++) {
      years.push(currentYear - i);
    }
    return years;
  };

  // 계좌 데이터가 없는 경우 스켈레톤 표시
  if (isInitialLoad || (!hasAccountData && !hasHoldingsData)) {
    return (
      <Card
        title={
          <Space>
            <LineChartOutlined />
            <span>{title}</span>
          </Space>
        }
        extra={
          <Skeleton.Input style={{ width: '200px', height: '32px' }} active />
        }
      >
        <div style={{ textAlign: 'center', marginBottom: '16px' }}>
          <Space size="small">
            {[1, 2, 3, 4, 5].map(i => (
              <Skeleton.Button key={i} style={{ width: '80px' }} active size="small" />
            ))}
          </Space>
        </div>
        
        <StatsSkeleton />
        <ChartSkeleton />
      </Card>
    );
  }

  return (
    <Card
      title={
        <Space>
          <LineChartOutlined />
          <span>{title}</span>
        </Space>
      }
      extra={
        <Space>
          <Select
            value={chartMode}
            style={{ width: 140 }}
            onChange={setChartMode}
            disabled={loading}
            size="small"
          >
            <Select.Option value="total">전체 포트폴리오</Select.Option>
            <Select.Option value="account">계좌별 보기</Select.Option>
          </Select>
          <Select
            placeholder="년도 선택"
            style={{ width: 120 }}
            onChange={setYearRange}
            disabled={loading}
            size="small"
          >
            {getAvailableYears().map(year => (
              <Select.Option key={year} value={year}>
                {year}년
              </Select.Option>
            ))}
          </Select>
          <RangePicker
            value={dateRange}
            onChange={handleDateRangeChange}
            format="YYYY-MM-DD"
            disabled={loading}
            allowClear={false}
          />
        </Space>
      }
    >
      {/* Preset Date Range Buttons */}
      <div style={{ textAlign: 'center', marginBottom: '16px' }}>
        <Button.Group size="small">
          <Button 
            onClick={setThisMonth}
            loading={loading}
            disabled={loading}
          >
            이번달
          </Button>
          <Button 
            onClick={setLastMonth}
            loading={loading}
            disabled={loading}
          >
            최근 한달
          </Button>
          <Button 
            onClick={setLastThreeMonths}
            loading={loading}
            disabled={loading}
          >
            최근 석달
          </Button>
          <Button 
            onClick={setLastSixMonths}
            loading={loading}
            disabled={loading}
          >
            최근 여섯달
          </Button>
          <Button 
            onClick={setLastYear}
            loading={loading}
            disabled={loading}
          >
            최근 1년
          </Button>
        </Button.Group>
      </div>
      
      {error && (
        <Alert
          message="데이터 로드 실패"
          description={error}
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
        />
      )}

      {accounts.length === 0 && (
        <div style={{ textAlign: 'center', padding: '50px' }}>
          <BankOutlined style={{ fontSize: '48px', color: '#d9d9d9', marginBottom: '16px' }} />
          <Title level={4} type="secondary">등록된 계좌가 없습니다</Title>
          <Text type="secondary">계좌를 등록하여 포트폴리오 분석을 확인하세요.</Text>
        </div>
      )}

      {accounts.length > 0 && (
        <>
          {/* 로딩 중일 때 진행률 표시 */}
          {loading && (
            <div style={{ textAlign: 'center', marginBottom: '24px' }}>
              <Spin size="large" />
              <div style={{ marginTop: '16px' }}>
                <Text>포트폴리오 데이터 로딩 중... ({dataLoadingProgress}%)</Text>
              </div>
            </div>
          )}

          {/* 포트폴리오 통계 */}
          {portfolioStats ? (
            <>
              <Row gutter={16} style={{ marginBottom: 24 }}>
                <Col xs={12} sm={6}>
                  {hideAmounts ? (
                    <div>
                      <div style={{ color: 'rgba(0, 0, 0, 0.45)', fontSize: '14px', marginBottom: '8px' }}>시작 자산</div>
                      <div style={{ fontSize: '14px', fontWeight: 'bold' }}>******</div>
                    </div>
                  ) : (
                    <Statistic
                      title="시작 자산"
                      value={portfolioStats.firstDayAssets}
                      precision={0}
                      prefix="₩"
                      valueStyle={{ fontSize: '14px' }}
                    />
                  )}
                </Col>
                <Col xs={12} sm={6}>
                  {hideAmounts ? (
                    <div>
                      <div style={{ color: 'rgba(0, 0, 0, 0.45)', fontSize: '14px', marginBottom: '8px' }}>현재 자산</div>
                      <div style={{ fontSize: '14px', fontWeight: 'bold' }}>******</div>
                    </div>
                  ) : (
                    <Statistic
                      title="현재 자산"
                      value={portfolioStats.lastDayAssets}
                      precision={0}
                      prefix="₩"
                      valueStyle={{ fontSize: '14px' }}
                    />
                  )}
                </Col>
                <Col xs={12} sm={6}>
                  {hideAmounts ? (
                    <div>
                      <div style={{ color: 'rgba(0, 0, 0, 0.45)', fontSize: '14px', marginBottom: '8px' }}>최고 자산</div>
                      <div style={{ fontSize: '14px', fontWeight: 'bold', color: '#52c41a' }}>******</div>
                    </div>
                  ) : (
                    <Statistic
                      title="최고 자산"
                      value={portfolioStats.maxTotalAssets}
                      precision={0}
                      prefix="₩"
                      valueStyle={{ fontSize: '14px', color: '#52c41a' }}
                    />
                  )}
                </Col>
                <Col xs={12} sm={6}>
                  {hideAmounts ? (
                    <div>
                      <div style={{ color: 'rgba(0, 0, 0, 0.45)', fontSize: '14px', marginBottom: '8px' }}>최저 자산</div>
                      <div style={{ fontSize: '14px', fontWeight: 'bold', color: '#ff4d4f' }}>******</div>
                    </div>
                  ) : (
                    <Statistic
                      title="최저 자산"
                      value={portfolioStats.minTotalAssets}
                      precision={0}
                      prefix="₩"
                      valueStyle={{ fontSize: '14px', color: '#ff4d4f' }}
                    />
                  )}
                </Col>
              </Row>

              <Row gutter={16} style={{ marginBottom: 24 }}>
                <Col xs={24} sm={8}>
                  {hideAmounts ? (
                    <div>
                      <div style={{ color: 'rgba(0, 0, 0, 0.45)', fontSize: '14px', marginBottom: '8px' }}>자산 변화</div>
                      <div style={{ display: 'flex', alignItems: 'center', color: portfolioStats.totalAssetsChange >= 0 ? '#3f8600' : '#cf1322' }}>
                        {portfolioStats.totalAssetsChange >= 0 ? <ArrowUpOutlined style={{ marginRight: '4px' }} /> : <ArrowDownOutlined style={{ marginRight: '4px' }} />}
                        <span style={{ fontSize: '16px', fontWeight: 'bold' }}>******</span>
                      </div>
                    </div>
                  ) : (
                    <Statistic
                      title="자산 변화"
                      value={Math.abs(portfolioStats.totalAssetsChange)}
                      precision={0}
                      prefix={portfolioStats.totalAssetsChange >= 0 ? <ArrowUpOutlined /> : <ArrowDownOutlined />}
                      suffix="₩"
                      valueStyle={{
                        color: portfolioStats.totalAssetsChange >= 0 ? '#3f8600' : '#cf1322',
                        fontSize: '16px'
                      }}
                    />
                  )}
                </Col>
                <Col xs={24} sm={8}>
                  {hideAmounts ? (
                    <div>
                      <div style={{ color: 'rgba(0, 0, 0, 0.45)', fontSize: '14px', marginBottom: '8px' }}>자산 변화율</div>
                      <div style={{ display: 'flex', alignItems: 'center', color: portfolioStats.totalAssetsChangePercent >= 0 ? '#3f8600' : '#cf1322' }}>
                        {portfolioStats.totalAssetsChangePercent >= 0 ? <ArrowUpOutlined style={{ marginRight: '4px' }} /> : <ArrowDownOutlined style={{ marginRight: '4px' }} />}
                        <span style={{ fontSize: '16px', fontWeight: 'bold' }}>***</span>
                      </div>
                    </div>
                  ) : (
                    <Statistic
                      title="자산 변화율"
                      value={Math.abs(portfolioStats.totalAssetsChangePercent)}
                      precision={2}
                      prefix={portfolioStats.totalAssetsChangePercent >= 0 ? <ArrowUpOutlined /> : <ArrowDownOutlined />}
                      suffix="%"
                      valueStyle={{
                        color: portfolioStats.totalAssetsChangePercent >= 0 ? '#3f8600' : '#cf1322',
                        fontSize: '16px'
                      }}
                    />
                  )}
                </Col>
                <Col xs={24} sm={8}>
                  {hideAmounts ? (
                    <div>
                      <div style={{ color: 'rgba(0, 0, 0, 0.45)', fontSize: '14px', marginBottom: '8px' }}>현재 총 손익</div>
                      <div style={{ display: 'flex', alignItems: 'center', color: portfolioStats.currentPnL >= 0 ? '#3f8600' : '#cf1322' }}>
                        {portfolioStats.currentPnL >= 0 ? <ArrowUpOutlined style={{ marginRight: '4px' }} /> : <ArrowDownOutlined style={{ marginRight: '4px' }} />}
                        <span style={{ fontSize: '16px', fontWeight: 'bold' }}>******</span>
                      </div>
                    </div>
                  ) : (
                    <Statistic
                      title="현재 총 손익"
                      value={Math.abs(portfolioStats.currentPnL)}
                      precision={0}
                      prefix={portfolioStats.currentPnL >= 0 ? <ArrowUpOutlined /> : <ArrowDownOutlined />}
                      suffix="₩"
                      valueStyle={{
                        color: portfolioStats.currentPnL >= 0 ? '#3f8600' : '#cf1322',
                        fontSize: '16px'
                      }}
                    />
                  )}
                </Col>
              </Row>
            </>
          ) : (
            !loading && <StatsSkeleton />
          )}

          {/* 차트 */}
          <Spin spinning={loading}>
            {chartData.series && chartData.series.length > 0 ? (
              <div>
                <div style={{ marginBottom: '16px', padding: '8px', backgroundColor: '#f5f5f5', borderRadius: '4px' }}>
                  <Text style={{ fontSize: '12px', color: '#666' }}>
                    디버그: 모드 {chartMode}, 시리즈 {chartData.series.length}개, 첫 번째 시리즈 데이터 {chartData.series[0]?.data?.length || 0}개
                  </Text>
                </div>
                <ReactApexChart
                  key={`portfolio-chart-${chartMode}-${chartData.categories?.length}-${chartData.series?.[0]?.data?.length}`}
                  options={getChartOptions()}
                  series={chartData.series}
                  type="line"
                  height={400}
                />
              </div>
            ) : (
              !loading && (
                <div style={{ textAlign: 'center', padding: '50px' }}>
                  <CalendarOutlined style={{ fontSize: '48px', color: '#d9d9d9', marginBottom: '16px' }} />
                  <Title level={4} type="secondary">데이터가 없습니다</Title>
                  <Text type="secondary">선택한 기간에 포트폴리오 데이터가 없습니다.</Text>
                  <div style={{ marginTop: '16px', fontSize: '12px', color: '#999' }}>
                    디버그: chartData.series = {JSON.stringify(chartData.series?.length || 'undefined')}
                  </div>
                </div>
              )
            )}
          </Spin>
        </>
      )}
    </Card>
  );
};

export default PortfolioAnalysisChart;
